import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import useAutoFocus from '../../../hooks/useAutoFocus';

/**
 * F-60 / REQ-6118 (#606, #629): the ADMIN OVERRIDE dialog — a privileged actor
 * clearing ANOTHER reviewer's recorded decision (`POST /items/{id}/approval/
 * override`, `core.requirements.feedback.override_approval`).
 *
 * Mirrors `ApprovalReasonDialog`'s mandatory-non-empty-reason pattern (#603's
 * `reason`/`justification` dialogs) rather than inventing a new one: the core
 * refuses an empty reason with a 400 regardless of what any surface sends, and the
 * reasonless-write 400 was already hit once on the item-detail Approve button
 * (INT-4) — this dialog exists so the click here is never attempted without one.
 *
 * Deliberately a SEPARATE component from `ApprovalReasonDialog` rather than a third
 * `mode`: that dialog's two modes both collect text for the CALLER's own decision
 * (reject / approve-over-objection); this one clears SOMEONE ELSE's, which is a
 * different action with different copy (naming the reviewer being overridden) —
 * conflating them risked the wrong confirmation copy reaching the wrong action.
 */
function AdminOverrideDialog({ open, reviewer, itemTitle, onCancel, onConfirm }) {
  const [text, setText] = useState('');
  const [touched, setTouched] = useState(false);
  // #793: reuses the SAME ref for the empty-on-submit focus() below AND the
  // reopen-focuses-the-field behaviour `autoFocus` used to (incompletely,
  // first-mount-only) provide — one ref, not two. See hooks/useAutoFocus.
  const inputRef = useAutoFocus(open);

  useEffect(() => {
    if (open) { setText(''); setTouched(false); }
  }, [open]);

  const empty = text.trim().length === 0;

  const submit = () => {
    setTouched(true);
    if (empty) { if (inputRef.current) inputRef.current.focus(); return; }
    onConfirm(text.trim());
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth
            data-testid="override-approval-dialog">
      <DialogTitle>{`Override ${reviewer || 'reviewer'}’s decision`}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
          {`This clears ${reviewer || 'this reviewer'}’s recorded decision on “${itemTitle}” and records who overrode it, and why — it appears in the History feed as an override, naming both of you. The decision is removed, not merely annotated.`}
        </Typography>
        <TextField
          inputRef={inputRef}
          multiline
          minRows={3}
          fullWidth
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => setTouched(true)}
          error={touched && empty}
          helperText={touched && empty
            ? 'A reason is required to override a decision.'
            : ' '}
          placeholder="Why is this decision being overridden?"
          /* #646: see ApprovalReasonDialog — `inputProps` is silently dropped by
             @mui/material v9's slot-based TextField; `slotProps.htmlInput` is the v9
             spelling used everywhere else in this codebase. */
          slotProps={{ htmlInput: { 'data-testid': 'override-reason-input' } }}
        />
      </DialogContent>
      <DialogActions>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onCancel} data-testid="override-cancel">Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={submit}
          disabled={empty}
          data-testid="override-confirm"
        >
          Override
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AdminOverrideDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  reviewer: PropTypes.string,
  itemTitle: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default AdminOverrideDialog;
